import { useCallback, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Small hook that fetches an event's leaderboard, renders a hidden share
 * card to the DOM (via `payload` state), waits for it to mount, converts to
 * PNG and triggers a download.
 *
 * Consumers must render the returned `<EventLeaderboardShareCard>` inside
 * an off-screen container using the returned `payload` + `nodeRef`.
 */
export default function useDownloadEventLeaderboard() {
  const [payload, setPayload] = useState(null); // { event, entries } | null
  const [busyEventId, setBusyEventId] = useState(null);
  const nodeRef = useRef(null);

  const download = useCallback(async (eventId, { fetchFresh = true, snapshot, gender = "" } = {}) => {
    setBusyEventId(eventId);
    try {
      let data = snapshot;
      if (fetchFresh || !data) {
        const res = await api.get(`/events/${eventId}/leaderboard`);
        data = res.data;
      }
      if (gender) {
        const entries = (data.entries || [])
          .filter((e) => (e.gender || "").charAt(0).toUpperCase() === gender)
          .map((e, i) => ({ ...e, rank: i + 1 }));
        data = { ...data, entries, gender };
      }
      // Trigger a render of the hidden card so `nodeRef` is populated.
      setPayload(data);
      // Wait two paint cycles so React commits, layout runs, and any icons paint.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (!nodeRef.current) throw new Error("Share card failed to mount");
      const dataUrl = await toPng(nodeRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#05070c",
        skipFonts: true,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      const safe = (data?.event?.name || "event").replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
      const genderTag = { M: "_male", F: "_female" }[data?.gender] || "";
      a.download = `gps-leaderboard-${safe}${genderTag}.png`;
      a.click();
      toast.success("LEADERBOARD PNG DOWNLOADED");
    } catch (err) {
      toast.error(`Export failed: ${err?.message || err}`);
    } finally {
      setPayload(null);
      setBusyEventId(null);
    }
  }, []);

  return { download, payload, busyEventId, nodeRef };
}
